import type { IAttributeTemplate, IBounds, ILinkByUid } from "../data-object";
import type { GemdCriterium } from "../table-config";
import { isOfType } from "../utils";

export interface TableConfigIdentifier {
  id: string;
  version: string | number;
}

interface BaseColumn<Type extends string> {
  type: Type;
  data_source: string;
}

interface ComponentQuantityColumn extends BaseColumn<"component_quantity_column"> {
  component_name: string;
  normalize: boolean;
}

interface ConcatColumn extends BaseColumn<"concat_column"> {
  delimiter: string;
  subcolumn: Column;
}

interface FlatCompositionColumn extends BaseColumn<"flat_composition_column"> {
  sort_order: "alphabetical" | "quantity";
}

type IdentityColumn = BaseColumn<"identity_column">;

interface MeanColumn extends BaseColumn<"mean_column"> {
  target_units?: string;
}

interface MolecularStructureColumn extends BaseColumn<"molecular_structure_column"> {
  format: "inchi" | "smiles";
}

interface MostLikelyCategoryColumn extends BaseColumn<"most_likely_category_column"> {
  suffix: "Mode";
}

interface MostLikelyProbabilityColumn extends BaseColumn<"most_likely_probability_column"> {
  suffix: "P(mode)";
}

interface NthBiggestComponentNameColumn extends BaseColumn<"biggest_component_name_column"> {
  n: number;
}

interface NthBiggestComponentQuantityColumn extends BaseColumn<"biggest_component_quantity_column"> {
  n: number;
  normalize: boolean;
}

type OriginalUnitsColumn = BaseColumn<"original_units_column">;

interface QuantileColumn extends BaseColumn<"quantile_column"> {
  quantile: number;
  target_units?: string;
}
interface StdDevColumn extends BaseColumn<"std_dev_column"> {
  target_units?: string;
}

export type Column =
  | ComponentQuantityColumn
  | ConcatColumn
  | FlatCompositionColumn
  | IdentityColumn
  | MeanColumn
  | MolecularStructureColumn
  | MostLikelyCategoryColumn
  | MostLikelyProbabilityColumn
  | NthBiggestComponentNameColumn
  | NthBiggestComponentQuantityColumn
  | OriginalUnitsColumn
  | QuantileColumn
  | StdDevColumn;

export const isIdentityColumn = isOfType<IdentityColumn, Column>(
  "identity_column"
);
export const isMeanColumn = isOfType<MeanColumn, Column>("mean_column");
export const isOriginalUnitsColumn = isOfType<OriginalUnitsColumn, Column>(
  "original_units_column"
);

interface Row {
  type: "material_run_by_template";
  tags?: string[];
  templates: ILinkByUid[];
}

interface BaseVariable<Type extends string> {
  type: Type;
  name: string;
  headers: string[];
}

type TypeSelector = "any" | "prefer_run" | "run_only" | "spec_only";

interface BaseAttributeTemplateVariable<
  Type extends string,
> extends BaseVariable<Type> {
  attribute_constraints?: [ILinkByUid, IBounds][];
  attribute_template_type: IAttributeTemplate["type"];
  type_selector: TypeSelector;
}

interface AttributeByTemplate extends BaseAttributeTemplateVariable<"attribute_by_template"> {
  template: ILinkByUid;
}

interface AttributeByTemplateAfterProcessTemplate extends BaseAttributeTemplateVariable<"attribute_after_process"> {
  attribute_template: ILinkByUid;
  process_template: ILinkByUid;
}

interface AttributeByTemplateAndObjectTemplate extends BaseAttributeTemplateVariable<"attribute_by_object"> {
  attribute_template: ILinkByUid;
  object_template: ILinkByUid;
}

interface AttributeInOutput extends BaseAttributeTemplateVariable<"attribute_in_trunk"> {
  attribute_template: ILinkByUid;
  process_templates: ILinkByUid[];
}

interface IngredientIdentifierByProcessTemplateAndName extends BaseVariable<"ing_id_by_process_and_name"> {
  ingredient_name: string;
  process_template: ILinkByUid;
  scope: string;
  type_selector: TypeSelector;
}

interface IngredientIdentifierInOutput extends BaseVariable<"ing_id_in_output"> {
  ingredient_name: string;
  process_templates: ILinkByUid[];
  scope: string;
  type_selector: TypeSelector;
}

interface IngredientLabelByProcessAndName extends BaseVariable<"ing_label_by_process_and_name"> {
  ingredient_name: string;
  process_template: ILinkByUid;
  type_selector: TypeSelector;
}

interface IngredientLabelsSetByProcessAndName extends BaseVariable<"ing_labels_set_by_process_and_name"> {
  ingredient_name: string;
  process_template: ILinkByUid;
}

interface IngredientLabelsSetInOutput extends BaseVariable<"ing_label_set_in_output"> {
  ingredient_name: string;
  process_templates: ILinkByUid[];
}

type IngredientQuantityDimension = "absolute" | "mass" | "number" | "volume";
type DimensionAndUnits =
  | {
      quantity_dimension: Omit<IngredientQuantityDimension, "absolute">;
      unit?: string;
    }
  | {
      quantity_dimension: "absolute";
      unit: string;
    };

type IngredientQuantityByProcessAndName =
  BaseVariable<"ing_quantity_by_process_and_name"> & {
    ingredient_name: string;
    process_template: ILinkByUid;
    type_selector: TypeSelector;
  } & DimensionAndUnits;

type IngredientQuantityInOutput = BaseVariable<"ing_quantity_in_output"> & {
  ingredient_name: string;
  process_templates: ILinkByUid[];
  type_selector: TypeSelector;
} & DimensionAndUnits;

interface LocalAttribute extends BaseAttributeTemplateVariable<"local_attribute"> {
  template: ILinkByUid;
}

interface LocalAttributeAndObject extends BaseAttributeTemplateVariable<"local_attribute_and_object"> {
  template: ILinkByUid;
  object_template: ILinkByUid;
}

interface LocalIngredientIdentifier extends BaseVariable<"local_ing_id"> {
  ingredient_name: string;
  scope: string;
  type_selector: TypeSelector;
}

interface LocalIngredientLabelSet extends BaseVariable<"local_ing_label_set"> {
  ingredient_name: string;
}

type LocalIngredientQuantity = BaseVariable<"local_ing_quantity"> & {
  ingredient_name: string;
  scope: string;
  type_selector: TypeSelector;
} & DimensionAndUnits;

interface TerminalMaterialIdentifier extends BaseVariable<"root_id"> {
  scope: string;
}

interface TerminalMaterialInfo extends BaseVariable<"root_info"> {
  /**
   * This is typed as just a string, but the only values that work at this time are:
   *   `"NAME"`, `"SAMPLE_TYPE"`, and `"TAGS"`.
   *
   * It is typed an enum in the internal docs (see `RootInfo` variable inside POST body):
   *   https://docs.corp.citrine.io/platform-backend/ara-tables/index.html#tag/Table-Configs/paths/~1table-configs/post
   *
   * But in external facing documentation, it is just a string:
   *   https://citrineinformatics.github.io/citrine-python/reference/citrine.gemtables.variables.html#citrine.gemtables.variables.TerminalMaterialInfo
   *
   * Lenore says they tried to make it an enum but didn't want to cause breaking changes.
   *
   * **Note:** Not super relevant at this time for UI work, but if the field is `"SAMPLE_TYPE"`,
   * values in the CSV column will be `"experimental"`, `"production"`, `"virtual"`, or `"unknown"`
   * as documented here:
   *   https://citrineinformatics.github.io/gemd-docs/specification/objects/#material-run
   */
  field: string;
}

interface XOR extends BaseVariable<"xor"> {
  variables: Variable[];
}

type Variable =
  | AttributeByTemplate
  | AttributeByTemplateAfterProcessTemplate
  | AttributeByTemplateAndObjectTemplate
  | AttributeInOutput
  | IngredientIdentifierByProcessTemplateAndName
  | IngredientIdentifierInOutput
  | IngredientLabelByProcessAndName
  | IngredientLabelsSetByProcessAndName
  | IngredientLabelsSetInOutput
  | IngredientQuantityByProcessAndName
  | IngredientQuantityInOutput
  | LocalAttribute
  | LocalAttributeAndObject
  | LocalIngredientIdentifier
  | LocalIngredientLabelSet
  | LocalIngredientQuantity
  | TerminalMaterialIdentifier
  | TerminalMaterialInfo
  | XOR;

type IngredientIdentifierVariable =
  | IngredientIdentifierByProcessTemplateAndName
  | IngredientIdentifierInOutput
  | LocalIngredientIdentifier;

export const isIngredientIdentifierVariable = isOfType<
  IngredientIdentifierVariable,
  Variable
>("ing_id_by_process_and_name", "ing_id_in_output", "local_ing_id");

type AttributeTemplateVariable =
  | AttributeByTemplate
  | AttributeByTemplateAfterProcessTemplate
  | AttributeByTemplateAndObjectTemplate
  | AttributeInOutput
  | LocalAttribute
  | LocalAttributeAndObject;

export const isAttributeTemplateVariable = isOfType<
  AttributeTemplateVariable,
  Variable
>(
  "attribute_by_template",
  "attribute_after_process",
  "attribute_by_object",
  "attribute_in_trunk",
  "local_attribute",
  "local_attribute_and_object"
);

export const isIngredientQuantityVariable = isOfType<
  | IngredientQuantityByProcessAndName
  | IngredientQuantityInOutput
  | LocalIngredientQuantity,
  Variable
>(
  "ing_quantity_by_process_and_name",
  "ing_quantity_in_output",
  "local_ing_quantity"
);

export const isTerminalMaterialIdentifierVariable = isOfType<
  TerminalMaterialIdentifier,
  Variable
>("root_id");

export const isTerminalMaterialInfoVariable = isOfType<
  TerminalMaterialInfo,
  Variable
>("root_info");

export type GenerationAlgorithm = "unspecified" | "multistep_materials";
export interface GEMTableConfig {
  definition: {
    id: string;
    deleted: boolean;
    create_time: number;
    created_by: string;
    update_time: number;
    updated_by: string;
  };
  version: {
    id: string;
    definition_id: string;
    version_number: number;
    ara_definition: {
      name: string;
      description: string;
      rows: Row[];
      columns: Column[];
      datasets: string[];
      variables: Variable[];
      gemd_query?: {
        criteria?: GemdCriterium[];
        datasets?: string[];
      };
      generation_algorithm?: GenerationAlgorithm;
    };
    deleted: boolean;
    create_time: number;
    created_by: string;
    update_time: number;
    updated_by: string;
  };
}
